/**
 * @file agent.js
 * @description XMBL AI Agent - Pure introspection-based system
 * @version 2.0.0
 */

import models from '../state/models.js'

const componentContext = require.context('../../components', true, /\.vue$/)
const componentSources = {}
componentContext.keys().forEach(key => {
  const componentPath = key.replace('./', '').replace('.vue', '')
  try {
    const rawSource = require(`!!raw-loader!../../components/${key.replace('./', '')}`)
    const source = rawSource.default || rawSource
    componentSources[componentPath] = source
  } catch (e) {
    // Skip if raw-loader fails
  }
})

const actionFileSources = {}
try {
  actionFileSources.actions = require('!!raw-loader!./actions.js').default
  actionFileSources.agent = require('!!raw-loader!./agent.js').default
  actionFileSources.auth = require('!!raw-loader!./auth.js').default
  actionFileSources.create = require('!!raw-loader!./create.js').default
  actionFileSources.crypto = require('!!raw-loader!./crypto.js').default
  actionFileSources.delete = require('!!raw-loader!./delete.js').default
  actionFileSources.query = require('!!raw-loader!./query.js').default
  actionFileSources.update = require('!!raw-loader!./update.js').default
  actionFileSources.util = require('!!raw-loader!./util.js').default
} catch (e) {
  console.warn('Could not load action sources:', e)
}

// Add mutations source
const mutationFileSources = {}
try {
  mutationFileSources.mutations = require('!!raw-loader!./../mutations.js').default
} catch (e) {
  console.warn('Could not load mutation sources:', e)
}

import layer from '../../agent/layer.js'

const state = {
  agentUserId: '5nA8rryyPedDsIvtWmTD0pRbann2',
  agentEmail: 'agent@xmbl.org',
  // lightweight layer-captured logs (console hooks, screenshots) - primary xmbl log store
  layerLogs: []
}

const mutations = {}

const debugLog = (commit, type, summary, data) => {
  const logs = [...(window.$store?.state?.terminal?.debugLogs || []), {
    timestamp: Date.now(),
    type,
    summary,
    data
  }]
  commit('set', {keys: ['terminal', 'debugLogs'], val: logs}, { root: true })
}

// Helper functions for composition, context, and validation
// const compose = function (type, data, task){}
// const validate = function (type, data){}
// const context = function (type, data){}

const actions = {
  async initAgent({ commit, rootState }) {
    try {
      console.log('Initializing agent...')
      debugLog(commit, 'init', 'Agent initialization started', {})
      // Initialize agent layer (hooks into console and store)
      try {
        if (typeof window !== 'undefined' && window.$store) {
          // attach to window for easy runtime use from components
          window.agentLayer = layer.init(window.$store)
        }
      } catch (e) {
        console.warn('[agent] failed to init layer:', e.message)
      }
      
      const agentUser = rootState.userlist?.[state.agentUserId]
      
      if (!agentUser) {
        console.log('Agent user does not exist, skipping creation for now')
        debugLog(commit, 'init', 'Agent user not found in userlist', { userId: state.agentUserId })
      } else {
        debugLog(commit, 'init', 'Agent user found', { user: agentUser })
      }
      
      console.log('✅ Agent initialized successfully')
    } catch (error) {
      console.error('❌ Agent initialization failed:', error)
      throw error
    }
  },

  // Primary xmbl app-facing log entry (used by agent.layer.sendLog)
  async xmblLog({ commit, rootState }, { type = 'unknown', summary = '', data = {}, meta = {} } = {}) {
    try {
      const entry = {
        timestamp: Date.now(),
        type,
        summary,
        data,
        meta
      }

      // add to human-visible debug console
      const logs = [...(rootState.terminal?.debugLogs || []), entry]
      commit('set', { keys: ['terminal', 'debugLogs'], val: logs }, { root: true })

      // store a copy in agent.layerLogs for programmatic access
      const layerLogs = [...(rootState.agent?.layerLogs || []), entry]
      commit('set', { keys: ['agent', 'layerLogs'], val: layerLogs }, { root: true })

      // keep layer log array reasonably bounded
      if (layerLogs.length > 1000) {
        commit('set', { keys: ['agent', 'layerLogs'], val: layerLogs.slice(-1000) }, { root: true })
      }

      return entry
    } catch (e) {
      console.warn('[agent] xmblLog failed:', e.message)
      return null
    }
  },
  async introspect() {
    // Get the runtime Vuex store instance directly
    const store =
      typeof window !== "undefined" && window.$store ? window.$store : null;
    
    if (!store || !store._actions) {
      console.warn("[Agent] No store instance found");
      return {
        timestamp: Date.now(),
        dispatchActions: [],
        error: "Store not available",
      };
    }

    // Helper to get the original action function from module
    const getOriginalAction = (store, actionName) => {
      const [moduleName, localAction] = actionName.includes("/")
        ? actionName.split("/")
        : [null, actionName];
      
      try {
        if (moduleName && store._modulesNamespaceMap) {
          const module = store._modulesNamespaceMap[moduleName + "/"];
          if (module && module._rawModule && module._rawModule.actions) {
            return module._rawModule.actions[localAction];
          }
        } else if (!moduleName && store._modules && store._modules.root) {
          const rootActions = store._modules.root._rawModule.actions;
          return rootActions ? rootActions[localAction] : null;
        }
      } catch (e) {
        // Fallback to wrapped handler
      }
      
      // For root actions, try to access the original function from the store's actions object
      if (!moduleName) {
        try {
          // Try to get the original function from the store's actions
          const originalActions = store._modules.root._rawModule.actions;
          if (originalActions && originalActions[localAction]) {
            return originalActions[localAction];
          }
        } catch (e) {
          // Continue to fallback
        }
      }
      
      // Fallback: use the wrapped handler
      const handlers = store._actions[actionName];
      return Array.isArray(handlers) ? handlers[0] : handlers;
    };

    // Helper to get the original mutation function from module
    const getOriginalMutation = (store, mutationName) => {
      const [moduleName, localMutation] = mutationName.includes("/")
        ? mutationName.split("/")
        : [null, mutationName];
      
      try {
        if (moduleName && store._modulesNamespaceMap) {
          const module = store._modulesNamespaceMap[moduleName + "/"];
          if (module && module._rawModule && module._rawModule.mutations) {
            return module._rawModule.mutations[localMutation];
          }
        } else if (!moduleName && store._modules && store._modules.root) {
          const rootMutations = store._modules.root._rawModule.mutations;
          return rootMutations ? rootMutations[localMutation] : null;
        }
      } catch (e) {
        // Fallback to wrapped handler
      }
      
      // For root mutations, try to access the original function from the store's mutations object
      if (!moduleName) {
        try {
          // Try to get the original function from the store's mutations
          const originalMutations = store._modules.root._rawModule.mutations;
          if (originalMutations && originalMutations[localMutation]) {
            return originalMutations[localMutation];
          }
        } catch (e) {
          // Continue to fallback
        }
      }
      
      // Fallback: use the wrapped handler
      const handlers = store._mutations[mutationName];
      return Array.isArray(handlers) ? handlers[0] : handlers;
    };


    // Infer type from parameter name patterns
    const inferTypeFromParameterName = (paramName, models) => {
      if (!paramName) return 'string';
      
      // Check for common patterns
      if (paramName.includes('keys') || paramName.includes('array') || paramName.includes('list')) {
        return 'array';
      }
      
      if (paramName.includes('data') || paramName.includes('object') || paramName.includes('config')) {
        return 'object';
      }
      
      if (paramName.includes('count') || paramName.includes('number') || paramName.includes('id')) {
        return 'number';
      }
      
      if (paramName.includes('flag') || paramName.includes('enabled') || paramName.includes('active')) {
        return 'boolean';
      }
      
      // Try to infer from models
      if (models) {
        const modelType = getDetailedTypeInfo(paramName, models);
        if (modelType.type !== 'string') {
          return modelType.type;
        }
      }
      
      return 'string';
    };

    // Get detailed type information for nested properties
    const getDetailedTypeInfo = (propertyPath, models) => {
      if (!propertyPath || !models) return { type: 'string', properties: {} };
      
      const pathParts = propertyPath.split('.');
      let currentModel = models;
      let fullPath = '';
      
      for (const part of pathParts) {
        if (currentModel && typeof currentModel === 'object' && currentModel[part]) {
          currentModel = currentModel[part];
          fullPath += (fullPath ? '.' : '') + part;
        } else {
          // Try to find partial matches or array patterns
          if (currentModel && typeof currentModel === 'object') {
            const arrayKeys = Object.keys(currentModel).filter(key => key.startsWith('[') && key.endsWith(']'));
            if (arrayKeys.length > 0) {
              const arrayKey = arrayKeys[0];
              const arrayModel = currentModel[arrayKey];
              if (arrayModel && typeof arrayModel === 'object') {
                currentModel = arrayModel;
                continue;
              }
            }
          }
          return { type: 'string', properties: {} };
        }
      }
      
      if (currentModel && typeof currentModel === 'object') {
        const properties = {};
        Object.keys(currentModel).forEach(key => {
          if (typeof currentModel[key] === 'function') {
            properties[key] = { type: currentModel[key].name.toLowerCase() };
          } else if (typeof currentModel[key] === 'object' && currentModel[key] !== null) {
            if (currentModel[key].arrayMap) {
              properties[key] = { type: 'array', description: currentModel[key].arrayMap };
            } else {
              properties[key] = { type: 'object', properties: getDetailedTypeInfo(key, { [key]: currentModel[key] }).properties };
            }
          } else {
            properties[key] = { type: typeof currentModel[key] };
          }
        });
        
        return {
          type: Object.keys(properties).length > 0 ? 'object' : 'string',
          properties: properties
        };
      }
      
      return { type: 'string', properties: {} };
    };

    // Smart split that respects nested braces and brackets
    const smartSplit = (str) => {
      const params = [];
      let current = '';
      let depth = 0;
      
      for (let i = 0; i < str.length; i++) {
        const char = str[i];
        
        if (char === '{' || char === '[' || char === '(') {
          depth++;
          current += char;
        } else if (char === '}' || char === ']' || char === ')') {
          depth--;
          current += char;
        } else if (char === ',' && depth === 0) {
          if (current.trim()) params.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      if (current.trim()) params.push(current.trim());
      return params;
    };
    
    // Helper to extract function parameter names from raw source or function
    const getFunctionParams = (func, rawSource = null, actionName = null) => {
      // If we have raw source for this action, parse from there to get real parameter names
      if (rawSource && actionName) {
        // Try to find the action definition in the raw source
        // Pattern: actionName: async ({ commit, dispatch, state }, payload) => {
        // or: async actionName({ commit, dispatch, state }, payload) {
        const patterns = [
          new RegExp(`${actionName}\\s*:\\s*async\\s*\\(([^)]+)\\)\\s*=>`, 'm'),
          new RegExp(`async\\s+function\\s+${actionName}\\s*\\(([^)]+)\\)`, 'm'),
          new RegExp(`async\\s+${actionName}\\s*\\(([^)]+)\\)`, 'm'),
          new RegExp(`${actionName}\\s*:\\s*\\(([^)]+)\\)\\s*=>`, 'm'),
          new RegExp(`function\\s+${actionName}\\s*\\(([^)]+)\\)`, 'm'),
          new RegExp(`${actionName}\\s*\\(([^)]+)\\)`, 'm')
        ];
        
        for (const pattern of patterns) {
          const match = rawSource.match(pattern);
          if (match && match[1]) {
            const paramsStr = match[1];
            const params = smartSplit(paramsStr).map((p) => {
              // Handle default values like payload = {}
              const withoutDefault = p.split("=")[0].trim();
              return withoutDefault;
            });
            
            if (params.length > 0) return params;
          }
        }
      }
      
      // Fallback: parse from bundled function (will have minified names)
      if (!func || typeof func !== "function") return [];
      
      const funcStr = func.toString();
      const match = funcStr.match(/\(([^)]*)\)/);
      
      if (!match || !match[1]) return [];
      
      // Parse parameters, handling destructuring
      const params = smartSplit(match[1]).map((p) => {
        // Handle default values like payload = {}
        return p.split("=")[0].trim();
      });
      
      return params;
    };

    // Helper to scan function body for methods object
    const scanForPayloadTypes = (func, payloadParamName, rawSource = null) => {
      if (!payloadParamName) return null;
      
      // Use raw source if available, otherwise fall back to bundled function
      const funcBody = rawSource || (func && typeof func === "function" ? func.toString() : null);
      if (!funcBody) return null;
      
      // ONLY check for methods[ pattern to detect actions that use methods object
      const usesMethodsPattern = /methods\[/.test(funcBody);
      
      if (!usesMethodsPattern) {
        return null;
      }
      
      // Runtime approach: Execute the function with a Proxy to intercept the methods object
      let capturedMethods = null;
      
      try {
        // Wrap the function execution to capture the methods object
        const mockState = store.state || {};
        const mockPayload = { type: "__introspect__", data: {} };
        
        // Try to execute just enough of the function to get the methods object
        // We'll use eval to create a scope where we can intercept variable assignments
        const funcSource = func.toString();
        
        // Extract just the methods object definition
        const methodsMatch = funcSource.match(
          /(?:const|let|var)\s+(methods)\s*=\s*(\{[\s\S]+?\n\s*\});?\s*(?:const|return|methods\[)/
        );
        
        if (methodsMatch) {
          try {
            // Evaluate just the methods object in a safe context
            const methodsObjCode = methodsMatch[2];
            // Create a function that returns the methods object
            const extractFn = new Function(
              "state",
              "payload",
              "store",
              "s",
              "fb",
              "fbBase",
              "fbusersbase",
              "Vue",
              "mu",
              "act",
              "t",
              "f",
              "version",
              "st",
              `try { return ${methodsObjCode}; } catch(e) { return null; }`
            );
            
            // Execute with minimal mocks to avoid errors
            const methodsObj = extractFn(
              mockState,
              mockPayload,
              store,
              mockState,
              {},
              {},
              {},
              {},
              {},
              {},
              true,
              false,
              1,
              mockState
            );

            if (methodsObj && typeof methodsObj === "object") {
              capturedMethods = methodsObj;
            }
          } catch (e) {
            // Fall back to string parsing
          }
        }
        
        // If we captured the methods object, extract the keys and scan each method for payload properties
        if (capturedMethods) {
          const types = {};
          Object.keys(capturedMethods).forEach((methodName) => {
            const method = capturedMethods[methodName];
            types[methodName] = {};
            
            // Scan the method for payload properties
            if (typeof method === "function") {
              const methodSource = method.toString();
              
              // Extract payload properties from method body
              const payloadProps = extractPayloadProperties(methodSource);
              if (Object.keys(payloadProps).length > 0) {
                Object.entries(payloadProps).forEach(([prop, metadata]) => {
                  types[methodName][prop] = metadata;
                });
              }
            }
          });
          return Object.keys(types).length > 0
            ? { types, requiresType: true }
            : null;
        }
      } catch (e) {
        // Runtime execution failed, fall through to parsing
      }
      
      // Fallback: Parse the source code
      try {
        // Look for methods object definition (handle transpiled code)
        const methodsObjMatch =
          funcBody.match(/(?:const|let|var)\s+methods\s*=\s*\{/) ||
                               funcBody.match(/methods\s*=\s*\{/) ||
                               funcBody.match(/var methods\s*=\s*\{/) ||
          funcBody.match(/const\s+methods\s*=\s*\{/);
        
        if (!methodsObjMatch) {
          return null;
        }
        
        // Find the complete object by counting braces
        let startIdx = methodsObjMatch.index + methodsObjMatch[0].length - 1;
        let depth = 1;
        let endIdx = startIdx + 1;
        let inString = false;
        let stringDelim = null;
        
        while (depth > 0 && endIdx < funcBody.length) {
          const char = funcBody[endIdx];
          const prevChar = funcBody[endIdx - 1];
          
          if (
            (char === '"' || char === "'" || char === "`") &&
            prevChar !== "\\"
          ) {
            if (!inString) {
              inString = true;
              stringDelim = char;
            } else if (char === stringDelim) {
              inString = false;
              stringDelim = null;
            }
          }
          
          if (!inString) {
            if (char === "{") depth++;
            if (char === "}") depth--;
          }
          
          endIdx++;
        }
        
        const objectBody = funcBody.substring(startIdx + 1, endIdx - 1);
        
        // Extract method names using regex patterns
        const types = {};
        
        // Look for method definitions with various patterns (including transpiled)
        const methodPatterns = [
          // methodName: (payload) => {
          /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*(?:async\s+)?\([^)]*\)\s*=>\s*\{/g,
          // methodName: function(payload) {
          /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*(?:async\s+)?function\s*\([^)]*\)\s*\{/g,
          // methodName: async (payload) => {
          /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*async\s*\([^)]*\)\s*=>\s*\{/g,
          // methodName: async function(payload) {
          /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*async\s*function\s*\([^)]*\)\s*\{/g,
          // Transpiled: methodName: function methodName() {
          /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*function\s+[a-zA-Z_$][a-zA-Z0-9_$]*\s*\([^)]*\)\s*\{/g,
          // Transpiled: methodName: function() {
          /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*function\s*\([^)]*\)\s*\{/g,
        ];
        
        for (const pattern of methodPatterns) {
          let match;
          while ((match = pattern.exec(objectBody)) !== null) {
            types[match[1]] = {};
          }
        }
        
        // Also scan each method for payload properties in the parsed version
        const methodKeys = Object.keys(types);
        methodKeys.forEach((methodName) => {
          // Try to find the method definition in the source - handle both arrow functions and regular functions
          const methodPatterns = [
            new RegExp(
              `${methodName}\\s*:\\s*(?:async\\s+)?\\([^)]*\\)\\s*=>\\s*\\{([\\s\\S]*?)\\}(?=\\s*[,}])`,
              "g"
            ),
            new RegExp(
              `${methodName}\\s*:\\s*(?:async\\s+)?function\\s*\\([^)]*\\)\\s*\\{([\\s\\S]*?)\\}(?=\\s*[,}])`,
              "g"
            ),
            new RegExp(
              `${methodName}\\s*:\\s*(?:async\\s+)?\\([^)]*\\)\\s*\\{([\\s\\S]*?)\\}(?=\\s*[,}])`,
              "g"
            ),
          ];

          let methodBody = null;
          for (const pattern of methodPatterns) {
            const match = funcBody.match(pattern);
            if (match) {
              methodBody = match[0];
              break;
            }
          }
          
          if (methodBody) {
            const payloadProps = extractPayloadProperties(methodBody);
            Object.entries(payloadProps).forEach(([prop, metadata]) => {
              types[methodName][prop] = metadata;
            });
          } else {
            // Enhanced fallback: search for the method name and extract properties from surrounding context
            const methodIndex = funcBody.indexOf(`${methodName}:`);
            if (methodIndex !== -1) {
              // Find the next method or end of object with better pattern matching
              const remainingText = funcBody.substring(methodIndex);
              
              // Look for the next method definition or end of object
              const nextMethodMatch = remainingText.match(
                /,\s*[a-zA-Z_$][a-zA-Z0-9_$]*\s*:/
              );
              const endOfObjectMatch = remainingText.match(/}\s*[,}]/);

              let endIndex = remainingText.length;
              if (nextMethodMatch) {
                endIndex = nextMethodMatch.index;
              } else if (endOfObjectMatch) {
                endIndex = endOfObjectMatch.index + 1;
              }

              const methodContext = remainingText.substring(0, endIndex);

              const payloadProps = extractPayloadProperties(methodContext);
              Object.entries(payloadProps).forEach(([prop, metadata]) => {
                types[methodName][prop] = metadata;
              });
            }
          }
        });

        return Object.keys(types).length > 0
          ? { types, requiresType: true }
          : null;
          } catch (e) {
        return null;
      }
    };

    // Helper to extract just a specific function's body from raw source
    const extractFunctionBody = (rawSource, actionName) => {
      if (!rawSource || !actionName) return null;
      
      // Try to find the function definition and extract its body
      const patterns = [
        // async function name({ }, payload) {
        new RegExp(`async\\s+function\\s+${actionName}\\s*\\([^)]*\\)\\s*\\{`, 'g'),
        // name: async ({ }, payload) => {
        new RegExp(`${actionName}\\s*:\\s*async\\s*\\([^)]*\\)\\s*=>\\s*\\{`, 'g'),
        // async name({ }, payload) {
        new RegExp(`async\\s+${actionName}\\s*\\([^)]*\\)\\s*\\{`, 'g'),
        // name: ({ }, payload) => {
        new RegExp(`${actionName}\\s*:\\s*\\([^)]*\\)\\s*=>\\s*\\{`, 'g'),
        // function name({ }, payload) {
        new RegExp(`function\\s+${actionName}\\s*\\([^)]*\\)\\s*\\{`, 'g')
      ];
      
      for (const pattern of patterns) {
        const match = pattern.exec(rawSource);
        if (match) {
          const startIdx = match.index + match[0].length - 1; // Position of opening brace
          
          // Find matching closing brace
          let depth = 1;
          let idx = startIdx + 1;
          let inString = false;
          let stringChar = null;
          let inComment = false;
          let inMultiLineComment = false;
          
          while (depth > 0 && idx < rawSource.length) {
            const char = rawSource[idx];
            const prevChar = idx > 0 ? rawSource[idx - 1] : '';
            const nextChar = idx < rawSource.length - 1 ? rawSource[idx + 1] : '';
            
            // Handle multi-line comments
            if (!inString && !inComment && char === '/' && nextChar === '*') {
              inMultiLineComment = true;
              idx += 2;
              continue;
            }
            if (inMultiLineComment && char === '*' && nextChar === '/') {
              inMultiLineComment = false;
              idx += 2;
              continue;
            }
            if (inMultiLineComment) {
              idx++;
              continue;
            }
            
            // Handle single-line comments
            if (!inString && !inComment && char === '/' && nextChar === '/') {
              inComment = true;
            }
            if (inComment && char === '\n') {
              inComment = false;
            }
            if (inComment) {
              idx++;
              continue;
            }
            
            // Handle strings
            if ((char === '"' || char === "'" || char === '`') && prevChar !== '\\') {
              if (!inString) {
                inString = true;
                stringChar = char;
              } else if (char === stringChar) {
                inString = false;
                stringChar = null;
              }
            }
            
            // Count braces only outside strings and comments
            if (!inString && !inComment && !inMultiLineComment) {
              if (char === '{') depth++;
              if (char === '}') depth--;
            }
            
            idx++;
          }
          
          if (depth === 0) {
            return rawSource.substring(startIdx, idx);
          }
        }
      }
      
      return null;
    };

    // Helper function to extract payload properties from function source with detailed metadata
    const extractPayloadProperties = (source) => {
      const properties = {};
      
      const addProperty = (propPath) => {
        if (!properties[propPath]) {
          properties[propPath] = {
            methods: [],
            usedBy: []
          };
        }
        return properties[propPath];
      };

      // Look for payload.property.method() patterns to track method calls
      const methodCallPattern = /payload\.([a-zA-Z_$][a-zA-Z0-9_$.]*?)\.([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g;
      let match;
      while ((match = methodCallPattern.exec(source)) !== null) {
        const propPath = match[1];
        const methodName = match[2];
        const prop = addProperty(propPath);
        
        if (!prop.methods.includes(methodName)) {
          prop.methods.push(methodName);
        }
        
        // Also add intermediate paths
        const pathParts = propPath.split(".");
        for (let i = 1; i < pathParts.length; i++) {
          addProperty(pathParts.slice(0, i).join("."));
        }
      }

      // Look for payload.property patterns (dot notation)
      const payloadPropPattern = /payload\.([a-zA-Z_$][a-zA-Z0-9_$]*(?:\.[a-zA-Z_$][a-zA-Z0-9_$]*)*)/g;
      while ((match = payloadPropPattern.exec(source)) !== null) {
        const fullPath = match[1];
        const cleanPath = fullPath.replace(/\([^)]*\)?$/g, "");
        addProperty(cleanPath);

        // Add intermediate paths
        const pathParts = cleanPath.split(".");
        for (let i = 1; i < pathParts.length; i++) {
          addProperty(pathParts.slice(0, i).join("."));
        }
      }
      
      // Look for payload["property"] and payload['property'] patterns
      const bracketPropPattern = /payload\[["']([^"']+)["']\]/g;
      while ((match = bracketPropPattern.exec(source)) !== null) {
        addProperty(match[1]);
      }
      
      // Look for destructuring patterns and track assignments
      const destructurePattern = /(const|let|var)\s*\{\s*([^}]+)\s*\}\s*=\s*payload/g;
      while ((match = destructurePattern.exec(source)) !== null) {
        const declarationType = match[1];
        const props = match[2].split(",").map((p) => {
          const parts = p.trim().split(":");
          const propName = parts[0].trim();
          const varName = parts.length > 1 ? parts[1].trim() : propName;
          return { propName, varName };
        });
        
        props.forEach(({ propName, varName }) => {
          if (propName && /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(propName)) {
            const prop = addProperty(propName);
            prop.usedBy.push([varName, 'assignment', `${declarationType} destructure`]);
          }
        });
      }
      
      // Look for assignment patterns: const/let/var x = payload.property
      const assignmentPattern = /(const|let|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=\s*payload\.([a-zA-Z_$][a-zA-Z0-9_$.]*)/g;
      while ((match = assignmentPattern.exec(source)) !== null) {
        const declarationType = match[1];
        const varName = match[2];
        const propPath = match[3];
        const prop = addProperty(propPath);
        prop.usedBy.push([varName, 'assignment', declarationType]);
      }
      
      // Look for function call patterns: someFunc(payload.property)
      const functionCallPattern = /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(\s*[^)]*payload\.([a-zA-Z_$][a-zA-Z0-9_$.]*)/g;
      while ((match = functionCallPattern.exec(source)) !== null) {
        const funcName = match[1];
        const propPath = match[2];
        const prop = addProperty(propPath);
        
        // Avoid duplicates
        const existing = prop.usedBy.find(u => u[0] === funcName && u[1] === 'function');
        if (!existing) {
          prop.usedBy.push([funcName, 'function', 'argument']);
        }
      }
      
      // Look for Object.method(payload.property) patterns
      const objectMethodPattern = /Object\.([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(\s*payload\.([a-zA-Z_$][a-zA-Z0-9_$.]*)/g;
      while ((match = objectMethodPattern.exec(source)) !== null) {
        const methodName = match[1];
        const propPath = match[2];
        const prop = addProperty(propPath);
        
        const existing = prop.usedBy.find(u => u[0] === `Object.${methodName}` && u[1] === 'function');
        if (!existing) {
          prop.usedBy.push([`Object.${methodName}`, 'function', 'argument']);
        }
      }
      
      // Look for nested property access patterns like payload.obj[prop] or payload.data[id]
      const nestedBracketPattern = /payload\.([a-zA-Z_$][a-zA-Z0-9_$]*)(?:\.([a-zA-Z_$][a-zA-Z0-9_$]*))?\[([a-zA-Z_$][a-zA-Z0-9_$]*)\]/g;
      while ((match = nestedBracketPattern.exec(source)) !== null) {
        const parent = match[1];
        const middle = match[2];
        const child = match[3];
        addProperty(parent);
        if (middle) {
          addProperty(`${parent}.${middle}`);
          addProperty(`${parent}.${middle}.${child}`);
        } else {
          addProperty(`${parent}.${child}`);
        }
      }

      return properties;
    };

    // Extract all registered actions from store._actions
    const dispatchActions = Object.keys(store._actions)
      .sort((a, b) => a.localeCompare(b))
      .map((actionName) => {
        const handlers = store._actions[actionName];
        const handlerCount = Array.isArray(handlers)
          ? handlers.length
          : handlers
          ? 1
          : 0;
        
        // Parse module/action name
        const [moduleName, localAction] = actionName.includes("/")
          ? actionName.split("/")
          : [null, actionName];

        // Get the original action function
        const originalAction = getOriginalAction(store, actionName);
        
        // Get raw source if available (for non-minified parameter names)
        // For root actions, check both the specific file and actions.js
        let rawSource = actionFileSources[localAction] || null;
        if (!rawSource && !moduleName) {
          rawSource = actionFileSources.actions || null;
        }
        
        // Extract parameters using raw source when available to avoid minified names
        const params = originalAction ? getFunctionParams(originalAction, rawSource, localAction) : [];
        
        // Vuex actions receive (context, payload) where context is { state, commit, dispatch, getters, rootState, rootGetters }
        // The second parameter is the actual payload
        const payloadParam = params.length > 1 ? params[1] : null;

        // Scan for methods object (use raw source when available for better parsing)
        const methodsInfo =
          originalAction && payloadParam
            ? scanForPayloadTypes(originalAction, payloadParam, rawSource)
            : null;
        
        // For actions without methods, scan for payload properties
        let payloadProperties = {};
        if (!methodsInfo && originalAction && payloadParam) {
          // Use raw source if available, otherwise fall back to bundled function
          let actionSource = originalAction.toString();
          
          // If we have raw source, try to extract just this function's body
          if (rawSource) {
            const functionBody = extractFunctionBody(rawSource, localAction);
            if (functionBody) {
              actionSource = functionBody;
            }
          }
          
          payloadProperties = extractPayloadProperties(actionSource);
        }
        
        const actionData = {
          name: actionName,
          module: moduleName || "root",
          action: localAction,
          handlerCount,
          params: params,
          payloadParam: payloadParam,
          signature: params.length > 0 ? `(${params.join(", ")})` : "()",
        };

        // Add types if methods object found
        if (methodsInfo) {
          actionData.types = methodsInfo.types;
          actionData.requiresType = methodsInfo.requiresType;
        } else if (Object.keys(payloadProperties).length > 0) {
          // Add payload properties for actions without methods
          actionData.payloadProperties = payloadProperties;
        }

        return actionData;
      });

    // Extract all registered mutations from store._mutations
    const mutations = Object.keys(store._mutations)
      .sort((a, b) => a.localeCompare(b))
      .map((mutationName) => {
        const handlers = store._mutations[mutationName];
        const handlerCount = Array.isArray(handlers)
          ? handlers.length
          : handlers
          ? 1
          : 0;
        
        // Parse module/mutation name
        const [moduleName, localMutation] = mutationName.includes("/")
          ? mutationName.split("/")
          : [null, mutationName];

        // Get the original mutation function
        const originalMutation = getOriginalMutation(store, mutationName);
        
        // Get raw source if available (for non-minified parameter names)
        let rawSource = mutationFileSources.mutations || null;
        
        // Extract parameters using raw source when available to avoid minified names
        const params = originalMutation ? getFunctionParams(originalMutation, rawSource, localMutation) : [];
        
        // Vuex mutations receive (state, payload) where state is the module state
        // The second parameter is the actual payload
        const payloadParam = params.length > 1 ? params[1] : null;

        // For mutations, scan for payload properties and enhance with detailed type info
        let payloadProperties = {};
        if (originalMutation && payloadParam) {
          // Use raw source if available, otherwise fall back to bundled function
          let mutationSource = originalMutation.toString();
          
          // If we have raw source, try to extract just this function's body
          if (rawSource) {
            const functionBody = extractFunctionBody(rawSource, localMutation);
            if (functionBody) {
              mutationSource = functionBody;
            }
          }
          
          payloadProperties = extractPayloadProperties(mutationSource);
          
          // Enhance payload properties with detailed type information from models
          if (models && Object.keys(payloadProperties).length > 0) {
            Object.keys(payloadProperties).forEach(prop => {
              const detailedType = getDetailedTypeInfo(prop, models);
              payloadProperties[prop] = {
                ...payloadProperties[prop],
                type: detailedType.type,
                properties: detailedType.properties,
                description: detailedType.description
              };
            });
          } else if (Object.keys(payloadProperties).length === 0 && payloadParam) {
            // If no payload properties found but we have a payload param, try to infer from the parameter name
            const inferredType = inferTypeFromParameterName(payloadParam, models);
            payloadProperties[payloadParam] = {
              type: inferredType,
              properties: {},
              description: `Inferred type for ${payloadParam}`
            };
          }
        }
        
        const mutationData = {
          name: mutationName,
          module: moduleName || "root",
          mutation: localMutation,
          handlerCount,
          params: params,
          payloadParam: payloadParam,
          signature: params.length > 0 ? `(${params.join(", ")})` : "()",
        };

        // Add payload properties for mutations
        if (Object.keys(payloadProperties).length > 0) {
          mutationData.payloadProperties = payloadProperties;
        }

        return mutationData;
      });

    // Scan for CSS classes in the system
    const scanStyleClasses = () => {
      const styleClasses = {};
      
      if (typeof window !== "undefined" && window.document) {
        // Get all stylesheets
        const stylesheets = Array.from(document.styleSheets);
        
        stylesheets.forEach((stylesheet, sheetIndex) => {
          try {
            // Get all CSS rules from the stylesheet
            const rules = Array.from(
              stylesheet.cssRules || stylesheet.rules || []
            );
            
            rules.forEach((rule, ruleIndex) => {
              if (rule.type === CSSRule.STYLE_RULE) {
                const selector = rule.selectorText;

                // Extract class selectors (starting with .)
                if (selector && selector.includes(".")) {
                  const classSelectors = selector
                    .split(",")
                    .map((s) => s.trim())
                    .filter((s) => s.startsWith("."));

                  classSelectors.forEach((classSelector) => {
                    // Clean up the class name (remove . and any pseudo-selectors)
                    const className = classSelector
                      .replace(/^\./, "")
                      .split(/[:\s]/)[0];
                    
                    if (className && !styleClasses[className]) {
                      styleClasses[className] = {
                        name: className,
                        selector: classSelector,
                        styles: {},
                        source: stylesheet.href || "inline",
                        sheetIndex,
                        ruleIndex,
                      };
                      
                      // Extract computed styles
                      try {
                        const computedStyle = rule.style;
                        for (let i = 0; i < computedStyle.length; i++) {
                          const property = computedStyle[i];
                          const value =
                            computedStyle.getPropertyValue(property);
                          styleClasses[className].styles[property] = value;
                        }
                      } catch (e) {
                        // Handle cross-origin stylesheet restrictions
                        styleClasses[className].styles = {
                          error:
                            "Cross-origin stylesheet - cannot access styles",
                        };
                      }
                    }
                  });
                }
              }
            });
          } catch (e) {
            // Handle cross-origin stylesheet restrictions
            console.warn(`Cannot access stylesheet ${sheetIndex}:`, e.message);
          }
        });

        // Also scan for dynamically added classes via style elements
        const styleElements = document.querySelectorAll("style");
        styleElements.forEach((styleEl, index) => {
          try {
            const cssText = styleEl.textContent || styleEl.innerText;
            const classMatches = cssText.match(
              /\.([a-zA-Z][a-zA-Z0-9_-]*)\s*\{[^}]*\}/g
            );

            if (classMatches) {
              classMatches.forEach((match) => {
                const classNameMatch = match.match(
                  /\.([a-zA-Z][a-zA-Z0-9_-]*)/
                );
                if (classNameMatch) {
                  const className = classNameMatch[1];
                  if (!styleClasses[className]) {
                    styleClasses[className] = {
                      name: className,
                      selector: `.${className}`,
                      styles: {},
                      source: "style-element",
                      elementIndex: index,
                      fullRule: match,
                    };
                  }
                }
              });
            }
          } catch (e) {
            console.warn(`Cannot access style element ${index}:`, e.message);
          }
        });
      }

      return styleClasses;
    };

    // Get items and functions from state
    const stateItems = store.state?.app?.basics?.items || []
    const stateFunctions = store.state?.app?.advanced?.functions || {}
    const stateItemTypes = store.state?.app?.itemTypes || []
    const statePages = store.state?.app?.pages ? Object.keys(store.state.app.pages) : []
    
    // Map functions by name using ref.functions and detect async functions
    const functionsByName = {}
    if (store.state?.ref?.functions) {
      Object.entries(store.state.ref.functions).forEach(([name, id]) => {
        if (stateFunctions[id]) {
          const func = stateFunctions[id]
          // Detect if function is async by checking if it has async commands
          const isAsync = func.commands && func.commands.some(cmd => {
            // Dispatch actions are typically async
            if (cmd.method && cmd.method.startsWith('dispatch.')) {
              return true
            }
            // Check if function is marked as async
            if (func.async) {
              return true
            }
            return false
          })
          
          // Add async property to function
          functionsByName[name] = {
            ...func,
            async: isAsync
          }
        }
      })
    }
    
    const introspection = {
      timestamp: Date.now(),
      storeAvailable: true,
      totalActions: dispatchActions.length,
      totalMutations: mutations.length,
      dispatchActions,
      mutations,
      components:
        typeof window !== "undefined" && window.app && window.app.$cp
          ? window.app.$cp
          : null,
      styleClasses: scanStyleClasses(),
      models: models,
      items: stateItems,
      itemTypes: stateItemTypes,
      functions: functionsByName,
      pages: statePages,
    };
    console.log({ introspection });
    
    // Store introspection data in the store state (memory only, NOT database)
    store.commit('set', {
      keys: ['agent', 'introspection'],
      val: introspection
    }, { root: true });
    
    return introspection;
  },
  async createConversation({ commit, dispatch, rootState }, { type = 'ai' } = {}) {
    debugLog(commit, 'conversation', 'Creating new conversation', {})
    console.log('[AI-TEST] Creating new conversation')
    
    const timestamp = Date.now()
    const dateStr = new Date(timestamp).toISOString().slice(0, 16).replace('T', ' ')
    const topic = `AI_${dateStr}`
    
    const conversationData = {
      topic,
      users: [rootState.auth.uid, state.agentUserId],
      type,
      banter: {
        [timestamp]: {
          userId: state.agentUserId,
          text: 'Hiii, what shall we build or update?',
          timestamp
        }
      },
      created: timestamp
    }
    
    const result = await dispatch('create', {
      type: 'msg',
      data: conversationData
    }, { root: true })
    
    // Handle case where create returns true (user not logged in)
    if (result === true) {
      console.warn('[AI-TEST] User not logged in, cannot create conversation')
      return null
    }
    
    const conversationId = result?.id || result
    console.log('[AI-TEST] Conversation created with ID:', conversationId)
    
    commit('set', {
      keys: ['terminal', 'currentConversationId'],
      val: conversationId
    }, { root: true })
    
    // Increment reactive count for terminal tabs
    const terminalTabsKey = `terminal_tabs_${conversationId}`
    commit('set', {
      keys: ['counts', terminalTabsKey],
      val: (rootState.counts[terminalTabsKey] || 0) + 1
    }, { root: true })
    
    await dispatch('query', { type: 'msgs' }, { root: true })
    
    debugLog(commit, 'conversation', 'Conversation created successfully', { conversationId })
    
    return conversationId
  },
  async sendUserMessage({ dispatch, commit, rootState }, { message, phase = 'intent' }) {
    debugLog(commit, 'prompt', 'User message received', { message, phase })
    
    let conversationId = rootState.terminal.currentConversationId
    if (!conversationId) {
      console.log('[AI] No active conversation, creating one...')
      conversationId = await dispatch('createConversation')
    }
    
    // Add user message to conversation
    await dispatch('update', {
      type: 'msgs',
      mId: conversationId,
      text: message,
      userId: rootState.auth.uid
    }, { root: true })
    
    // Load context.json for intent phase
    let contextData
    try {
      contextData = require('../../agent/context.json')
    } catch (e) {
      console.error('Failed to load context.json:', e)
      contextData = {
        system_data: {
          overview: "XMBL is a web application framework for building applications. It provides a cohesive set of functions and components for building web applications."
        },
        capabilities: {
          create: "Create a new page, component, item or function in an xmbl instance.",
          manage: "Manage app state, styles, users, pages, components, or functions from the introspection data in an xmbl instance.",
          delete: "Delete a page, component, item or function in an xmbl instance.",
          update: "Update a page, component, item, users, or functions in an xmbl instance."
        },
        workflow: {
          intent: {
            purpose: "Get the intent of the user's request and match it with XMBL's capabilities"
          }
        }
      }
    }
    
    // Ensure contextData has the expected structure
    if (!contextData.system_data) {
      contextData.system_data = { overview: "XMBL is a web application framework for building applications." }
    }
    if (!contextData.capabilities) {
      contextData.capabilities = {
        create: "Create a new page, component, item or function in an xmbl instance.",
        manage: "Manage app state, styles, users, pages, components, or functions from the introspection data in an xmbl instance.",
        delete: "Delete a page, component, item or function in an xmbl instance.",
        update: "Update a page, component, item, users, or functions in an xmbl instance."
      }
    }
    if (!contextData.workflow) {
      contextData.workflow = {
        intent: {
          purpose: "Get the intent of the user's request and match it with XMBL's capabilities"
        }
      }
    }
    
    // Get introspection data for component selection
    const introspection = rootState.agent?.introspection || {}
    
    // Prepare context for AI with comprehensive introspection data
    const aiContext = {
      user_prompt: message,
      system_overview: contextData.system_data?.overview || "XMBL is a web application framework for building applications.",
      capabilities: contextData.capabilities || {},
      workflow_intent: contextData.workflow?.intent || { purpose: "Get the intent of the user's request and match it with XMBL's capabilities" },
      introspection: {
        // Available components with their props and usage
        components: introspection.components || {},
        component_list: Object.keys(introspection.components || {}),
        
        // Available functions with signatures
        functions: introspection.functions || {},
        function_list: Object.keys(introspection.functions || {}),
        
        // Available actions (both dispatch actions and functions that can be called with :is="action")
        dispatchActions: introspection.dispatchActions || [],
        action_list: introspection.dispatchActions?.map(a => a.name) || [],
        
        // Available models for data structure
        models: introspection.models || {},
        model_list: Object.keys(introspection.models || {}),
        
        // Available pages and items
        pages: introspection.pages || {},
        page_list: Object.keys(introspection.pages || {}),
        items: introspection.items || {},
        item_list: Object.keys(introspection.items || {}),
        
        // Style classes for theming
        styleClasses: introspection.styleClasses || []
      }
    }
    
    debugLog(commit, 'context', 'Context prepared for intent phase with introspection', { aiContext })
    
    // Prepare current context (ensure it's an array) and include view-scope from agent layer if available
    const currentContext = Array.isArray(rootState.terminal.context) ? rootState.terminal.context.slice() : []
    let discoveredViewScope = null
    try {
      // Prefer any existing view_scope provided by the UI
      discoveredViewScope = currentContext.find(i => i && i.type === 'view_scope')
      if (!discoveredViewScope && typeof window !== 'undefined' && window.agentLayer && window.agentLayer.discoverViewScope) {
        const v = window.agentLayer.discoverViewScope()
        if (v) {
          discoveredViewScope = { type: 'view_scope', value: v, timestamp: Date.now() }
          currentContext.push(discoveredViewScope)
          // persist view scope into terminal.context for visibility
          commit('set', { keys: ['terminal', 'context'], val: currentContext }, { root: true })
        }
      }
    } catch (e) {
      // Non-fatal — AI should still function without view scope
      console.warn('[agent] discoverViewScope failed', e && e.message)
    }

    // Call AI for dynamic component discovery and assembly
    const aiResponse = await dispatch('discoverAndAssembleComponents', {
      userPrompt: message,
      contextData,
      introspection,
      currentContext: currentContext
    })
    
    // Parse AI response using XMBL validjson utility
    let parsedResponse
    try {
      // Use XMBL util to validate JSON first
      const isValidJson = await dispatch('validjson', aiResponse, { root: true })
      
      if (isValidJson) {
        parsedResponse = JSON.parse(aiResponse)
      } else {
        // Try to extract JSON from markdown code blocks
        const jsonMatch = aiResponse.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/)
        if (jsonMatch && jsonMatch[1]) {
          const extractedJson = jsonMatch[1]
          const isExtractedValid = await dispatch('validjson', extractedJson, { root: true })
          if (isExtractedValid) {
            parsedResponse = JSON.parse(extractedJson)
          }
        } else {
          // Try to find JSON object in the response
          const jsonObjectMatch = aiResponse.match(/\{[\s\S]*\}/)
          if (jsonObjectMatch) {
            const objectJson = jsonObjectMatch[0]
            const isObjectValid = await dispatch('validjson', objectJson, { root: true })
            if (isObjectValid) {
              parsedResponse = JSON.parse(objectJson)
            }
          }
        }
      }
      
      // If no valid JSON found, create fallback
      if (!parsedResponse) {
        throw new Error('No valid JSON found')
      }
    } catch (parseError) {
      console.warn('Failed to parse AI response:', parseError)
      // Fallback to basic response structure
      parsedResponse = {
        intent: message,
        response: aiResponse,
        capabilities: []
      }
    }
    
    // Validate recommendations and provide feedback for invalid choices
    const validation = await dispatch('agent/validateRecommendations', {
      components: parsedResponse.recommended_components || [],
      functions: parsedResponse.recommended_functions || [],
      actions: parsedResponse.recommended_actions || [],
      introspection
    }, { root: true })
    
    // Store the intent response with introspection recommendations and validation
    commit('set', {
      keys: ['terminal', 'lastPlan'],
      val: {
        phase: 'intent',
        intent: parsedResponse.intent,
        response: parsedResponse.response,
        capabilities: parsedResponse.capabilities,
        recommended_components: parsedResponse.recommended_components || [],
        recommended_functions: parsedResponse.recommended_functions || [],
        recommended_actions: parsedResponse.recommended_actions || [],
        validation: validation,
        timestamp: Date.now(),
        view_scope: discoveredViewScope ? discoveredViewScope.value : null
      }
    }, { root: true })
    
    // Store context used for this intent analysis
    const contextItems = [
      {
        type: 'system_overview',
        value: contextData.system_data?.overview || "XMBL is a web application framework for building applications.",
        timestamp: Date.now()
      },
      {
        type: 'capabilities',
        value: contextData.capabilities || {},
        timestamp: Date.now()
      },
      {
        type: 'workflow_intent',
        value: contextData.workflow?.intent || { purpose: "Get the intent of the user's request and match it with XMBL's capabilities" },
        timestamp: Date.now()
      }
    ]
    
    commit('set', {
      keys: ['terminal', 'context'],
      val: contextItems
    }, { root: true })
    
    // Attach view scope to the stored AI response so UI can present what the AI inspected
    try { parsedResponse.view_scope = discoveredViewScope ? discoveredViewScope.value : null } catch(e) {
      // Ignore errors when attaching view scope
    }

    // Parse and assemble XMBL functions and components from AI response
    if (parsedResponse.function) {
      await dispatch('agent/createFunctionFromAI', { functionDef: parsedResponse.function }, { root: true })
      debugLog(commit, 'function', 'Function created from AI response', { functionName: parsedResponse.function.name })
    }

    // Store AI response in conversation for history
    await dispatch('update', {
      type: 'msgs',
      mId: conversationId,
      text: JSON.stringify(parsedResponse),
      userId: '5nA8rryyPedDsIvtWmTD0pRbann2' // AI user ID
    }, { root: true })

    // If agent layer is available in the browser, also post the AI message using layer helper
    try {
      if (typeof window !== 'undefined' && window.agentLayer && window.agentLayer.postAIMessage) {
        await window.agentLayer.postAIMessage(conversationId, JSON.stringify(parsedResponse))
      }
    } catch (e) {
      // non-fatal
      console.warn('[agent] postAIMessage failed', e && e.message)
    }
    
    debugLog(commit, 'response', 'AI intent response received and stored', { parsedResponse })
    
  },

  async callAI({ commit, rootState }, { prompt }) {
    const model = rootState.terminal.selectedModel || 'llama3.2:latest'
    
    // Estimate input tokens (rough approximation: 1 token ≈ 4 characters)
    const estimatedInputTokens = Math.ceil(prompt.length / 4)
    
    debugLog(commit, 'ai', 'Calling AI model', { 
      model, 
      promptLength: prompt.length,
      estimatedInputTokens: estimatedInputTokens
    })
    
    try {
      const response = await fetch('http://localhost:11434/api/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: model,
          prompt: prompt,
          stream: false,
          options: {
            temperature: 0.7,
            top_p: 0.9,
            max_tokens: 2000
          }
        })
      })
      
      if (!response.ok) {
        throw new Error(`AI API error: ${response.status}`)
      }
      
      const data = await response.json()
      const aiResponse = data.response || ''
      
      // Get actual token counts from Ollama response
      const inputTokens = data.prompt_eval_count || estimatedInputTokens
      const outputTokens = data.eval_count || 0
      const totalTokens = inputTokens + outputTokens
      
      // Update terminal state with token counts
      commit('set', {
        keys: ['terminal', 'lastTokenCount'],
        val: totalTokens
      }, { root: true })
      
      debugLog(commit, 'ai', 'AI response received', { 
        model, 
        responseLength: aiResponse.length,
        inputTokens: inputTokens,
        outputTokens: outputTokens,
        totalTokens: totalTokens
      })
      
      return aiResponse
      
    } catch (error) {
      console.error('[AI] Error calling AI:', error)
      debugLog(commit, 'ai', 'AI call failed', { error: error.message })
      return `Error: ${error.message}`
    }
  },

  async acceptIntent({ commit, dispatch, rootState }) {
    debugLog(commit, 'intent', 'Intent accepted by user', {})
    
    // Ensure introspection is complete
    if (!rootState.agent?.introspection?.components || Object.keys(rootState.agent.introspection.components).length === 0) {
      await dispatch('agent/introspect', {}, { root: true })
    }
    
    // Move to compose phase
    commit('set', {
      keys: ['terminal', 'lastPlan', 'phase'],
      val: 'compose'
    }, { root: true })
    
    // Compose page structure
    await dispatch('agent/composePageStructure', {}, { root: true })
  },

  async rejectIntent({ commit, rootState }) {
    debugLog(commit, 'intent', 'Intent rejected by user', {})
    
    // Add current prompt/response to context for next attempt
    const currentPlan = rootState.terminal.lastPlan
    if (currentPlan) {
      const context = rootState.terminal.context || []
      context.push({
        type: 'rejected_intent',
        prompt: rootState.terminal.currentMessage,
        response: currentPlan.response,
        timestamp: Date.now()
      })
      
      commit('set', {
        keys: ['terminal', 'context'],
        val: context
      }, { root: true })
    }
    
    // Stay in intent phase for new prompt
    commit('set', {
      keys: ['terminal', 'lastPlan', 'phase'],
      val: 'intent'
    }, { root: true })
  },

  async getOllamaModels({ commit }) {
    debugLog(commit, 'ollama', 'Fetching available models', {})
    console.log('[Ollama] Fetching available models')
    try {
      const response = await fetch('http://localhost:11434/api/tags')
      const data = await response.json()
      
      const models = data.models?.map(m => m.name) || []
      console.log('[Ollama] Found models:', models)
      
      debugLog(commit, 'ollama', 'Models loaded', { models })
      
      commit('set', {
        keys: ['terminal', 'availableModels'],
        val: models
      }, { root: true })
      
      if (models.length > 0) {
        // Prefer llama models first
        const defaultModel = models.find(m => m.includes('llama')) || models[0]
        commit('set', {
          keys: ['terminal', 'selectedModel'],
          val: defaultModel
      }, { root: true })
      }
      
      return models
    } catch (error) {
      console.error('[Ollama] Error fetching models:', error)
      return []
    }
  },

  async callOllama({ commit, rootState }, { prompt, context }) {
    const model = rootState.terminal?.selectedModel || 'llama3.2:latest'
    
    debugLog(commit, 'ollama', 'Calling Ollama API', { 
      call: { model, promptLength: prompt.length } 
    })
    
    try {
      const response = await fetch('http://localhost:11434/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          prompt,
          context,
          stream: false
        })
      })
      
      const data = await response.json()
      
      debugLog(commit, 'ollama', 'Ollama API response received', { 
        result: { responseLength: data.response?.length } 
      })
      
      try {
        // Try direct JSON parse first
        const parsed = JSON.parse(data.response)
        return parsed
      } catch (e) {
        // If that fails, try to extract JSON from wrapped text (llama often wraps JSON)
        try {
          const jsonMatch = data.response.match(/\{[\s\S]*\}/)
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0])
            debugLog(commit, 'ollama', 'Extracted JSON from wrapped response', {})
            return parsed
          }
        } catch (e2) {
          // Fallback to text response
        }
        debugLog(commit, 'error', 'Failed to parse JSON response', { response: data.response.substring(0, 200) })
        return { plan: data.response, functions: [], components: [] }
      }
    } catch (error) {
      console.error('[Ollama] Error:', error)
      debugLog(commit, 'error', 'Ollama API call failed', { error: error.message })
      throw error
    }
  },

  // Dynamic component discovery and assembly through AI conversation
  async discoverAndAssembleComponents({ dispatch, commit }, { userPrompt, contextData, introspection, currentContext }) {
    debugLog(commit, 'ai', 'Starting dynamic component discovery and assembly', { userPrompt })
    
    // First, ask AI via the ai helper module so it can use agent functions if available
    const aiHelper = require('../../ai')

    // Ask the ai.ask module to analyze and recommend components
    // It will call the active model via dispatch('callAI') if available
    // First attempt via the ai helper
    let analysisResponse
    try {
      analysisResponse = await aiHelper.ask({ dispatch, commit }, { userPrompt, contextData, introspection, currentContext })
    } catch (e) {
      console.warn('[agent] ai.ask failed, falling back to callAI', e && e.message)
    }

    // If the helper didn't return a response, fall back to inline prompt
    if (!analysisResponse) {
      const analysisPrompt = `You are an AI assistant for XMBL. Analyze this user request and recommend specific components.

User Request: "${userPrompt}"

System Overview: ${contextData.system_data?.overview || "XMBL is a web application framework."}

Available Components with Descriptions:
${JSON.stringify(contextData.descriptions?.components || {}, null, 2)}

Available Functions: ${JSON.stringify(Object.keys(introspection.functions || {}), null, 2)}

Available Dispatch Actions: ${JSON.stringify(introspection.dispatchActions?.slice(0, 10) || [], null, 2)}

Current Context (including any view_scope objects): ${JSON.stringify(currentContext, null, 2)}

NOTE: Include a 'view_scope' object in your JSON that lists the UI elements you inspected on screen (if any). Also add a short 'introspection_instructions' field telling a developer or the application how to programmatically re-run or inspect the same view context (for example: call agent/introspect, check terminal.context for items with type 'view_scope', or call window.agentLayer.discoverViewScope()).

IMPORTANT: You must return ONLY valid JSON. No explanations, no markdown, no code blocks. Just the JSON object.

Return this exact JSON structure:
{
  "intent": "Clear description of what user wants",
  "response": "Natural language explanation of the solution",
  "capabilities": ["list", "of", "relevant", "capabilities"],
  "recommended_components": ["list", "of", "component", "names"],
  "recommended_functions": ["list", "of", "function", "names"],
  "recommended_actions": ["dispatch", "set"],
  "component_analysis": {
    "primary_component": "component_name",
    "component_properties": {
      "prop1": "value1",
      "prop2": "value2"
    },
    "nested_components": [
      {
        "component": "nested_component_name",
        "props": {
          "prop1": "value1"
        }
      }
    ]
  },
  "function": {
    "name": "functionName",
    "description": "What this function does",
    "inputs": {},
    "commands": [
      {"type": "app", "method": "commit.set", "payload": {"keys": ["app", "advanced", "functions", "hash"], "val": {}}},
      {"type": "app", "method": "dispatch.update", "payload": {"type": "app", "data": {}}}
    ],
    "output": {},
    "test": [],
    "show": {},
    "state": {}
  },
  "actionButton": {
    "fun": "functionName",
    "name": "Execute Function"
  }
}

IMPORTANT: If your response includes a function that can be executed, you MUST include both the "function" object (with complete function definition) AND the "actionButton" object (with fun matching function.name). The actionButton will be rendered as an interactive button in the chat interface.

FUNCTION UPDATE PATTERN: Functions can update other functions (or themselves) through state. Use commit.set to modify app.advanced.functions[hash] then dispatch.update to persist. Example: commands: [{"type": "app", "method": "commit.set", "payload": {"keys": ["app", "advanced", "functions", "{{targetHash}}"], "val": {{updatedFunction}}}}, {"type": "app", "method": "dispatch.update", "payload": {"type": "app", "data": {{appState}}}}]. This enables self-modifying and living functions.`
    
      analysisResponse = await dispatch('callAI', { prompt: analysisPrompt })
    }
    
    // Normalize analysis response into an object. Use ai feedback/sanity helpers if parsing fails.
    let analysisData
    try {
      if (typeof analysisResponse === 'object') {
        analysisData = analysisResponse
      } else {
        analysisData = JSON.parse(analysisResponse)
      }
    } catch (e) {
      // try to extract json blocks
      const jsonMatch = analysisResponse.match(/```json\n([\s\S]*?)\n```/) || analysisResponse.match(/```\n([\s\S]*?)\n```/)
      if (jsonMatch) {
        analysisData = JSON.parse(jsonMatch[1])
      } else {
        const jsonObjectMatch = analysisResponse.match(/\{[\s\S]*\}/)
        if (jsonObjectMatch) {
          analysisData = JSON.parse(jsonObjectMatch[0])
        } else {
          // Attempt a feedback loop using ai.feedback if available
          try {
            const aiHelper2 = require('../../ai')
            if (aiHelper2 && aiHelper2.feedback) {
              const fb = await aiHelper2.feedback({ dispatch, commit }, { prompt: analysisResponse, goal: null, maxIter: 2 })
              const candidate = fb && fb.resp ? fb.resp : null
              if (candidate) {
                try { analysisData = JSON.parse(candidate) } catch (e2) { /* ignore */ }
              }
            }
          } catch (fberr) {
            // ignore
          }

          // final fallback
          if (!analysisData) throw new Error('Failed to parse analysis response')
        }
      }

    // Post-process analysis data: attach view_scope if requested and enrich introspection info
    try {
      const aiHelper3 = require('../../ai')
      // ensure view scope is present
      let vs = null
      try { vs = await aiHelper3.viewstate({ dispatch, commit }, { rootState: { terminal: { context: currentContext } } }) } catch (e) { /* ignore */ }
      if (vs && !analysisData.view_scope) analysisData.view_scope = vs

      // attach a minimal introspection pointer if not present
      if (!analysisData.introspection_instructions) {
        try {
          const intros = await aiHelper3.introspect({ dispatch, commit }, { rootState: { agent: { introspection } } })
          analysisData.introspection_instructions = intros && intros.keys ? { context: intros.keys.context || [], models: intros.keys.models || [] } : { hint: 'call agent/introspect or use ai.introspect()' }
        } catch (ie) {
          analysisData.introspection_instructions = { hint: 'call agent/introspect or use ai.introspect()' }
        }
      }

      // run a quick sanity check and annotate warnings
      try {
        const sanity = require('../../ai/sanity')
        const chk = sanity(analysisData || {})
        if (chk && !chk.ok) {
          analysisData.sanity = chk
        }
      } catch (se) { /* ignore */ }
    } catch (postErr) {
      // non-fatal
    }
    }
    
    // Now ask AI to create the final component structure based on the analysis
    const structurePrompt = `Based on the analysis, create the final component structure for XMBL.

Analysis: ${JSON.stringify(analysisData, null, 2)}

User Request: "${userPrompt}"

Create a complete component structure with proper naming and all required properties. Use the component_analysis to build the final structure.

IMPORTANT: You must return ONLY valid JSON. No explanations, no markdown, no code blocks. Just the JSON object.

Return this exact JSON structure:
{
  "intent": "${analysisData.intent || userPrompt}",
  "response": "${analysisData.response || 'AI response'}",
  "capabilities": ${JSON.stringify(analysisData.capabilities || [])},
  "recommended_components": ${JSON.stringify(analysisData.recommended_components || [])},
  "recommended_functions": ${JSON.stringify(analysisData.recommended_functions || [])},
  "recommended_actions": ${JSON.stringify(analysisData.recommended_actions || [])},
  "component_structure": {
    "component": "layout",
    "props": {
      "header": {
        "components": [
          {
            "component": "icontent",
            "props": {
              "content": "## Intent Analysis\\n\\n**User Request:** ${userPrompt}"
            }
          }
        ]
      },
      "content": {
        "components": [
          {
            "component": "icontent", 
            "props": {
              "content": "### AI Response\\n\\n${analysisData.response || 'AI response'}"
            }
          }
        ]
      },
      "footer": {
        "components": [
          {
            "component": "button",
            "props": {
              "text": "Yes",
              "onClick": "acceptIntent"
            }
          },
          {
            "component": "button", 
            "props": {
              "text": "No",
              "onClick": "rejectIntent"
            }
          }
        ]
      }
    }
  }
}`
    
    const aiResponse = await dispatch('callAI', { prompt: structurePrompt })
    
    let finalData
    try {
      finalData = JSON.parse(aiResponse)
    } catch (e) {
      // Try to extract JSON from markdown
      const jsonMatch = aiResponse.match(/```json\n([\s\S]*?)\n```/) || aiResponse.match(/```\n([\s\S]*?)\n```/)
      if (jsonMatch) {
        finalData = JSON.parse(jsonMatch[1])
      } else {
        // Try to find JSON object in the response
        const jsonObjectMatch = aiResponse.match(/\{[\s\S]*\}/)
        if (jsonObjectMatch) {
          finalData = JSON.parse(jsonObjectMatch[0])
        } else {
          // Fallback to basic structure
          debugLog(commit, 'error', 'Failed to parse AI response, using fallback', { response: aiResponse.substring(0, 200) })
          finalData = {
            intent: userPrompt,
            response: "I'll help you create a user profile page with editable fields and save functionality using XMBL components.",
            capabilities: ["create", "update"],
            recommended_components: ["iform", "iinput", "button"],
            recommended_functions: ["save", "update"],
            recommended_actions: ["dispatch", "set"],
            component_structure: {
              component: "layout",
              props: {
                header: {
                  components: [
                    {
                      component: "icontent",
                      props: {
                        content: `## Intent Analysis\n\n**User Request:** ${userPrompt}`
                      }
                    }
                  ]
                },
                content: {
                  components: [
                    {
                      component: "icontent", 
                      props: {
                        content: "### AI Response\n\nI'll create a user profile page using XMBL's iform component with editable fields and save functionality."
                      }
                    }
                  ]
                },
                footer: {
                  components: [
                    {
                      component: "button",
                      props: {
                        text: "Yes",
                        onClick: "acceptIntent"
                      }
                    },
                    {
                      component: "button", 
                      props: {
                        text: "No",
                        onClick: "rejectIntent"
                      }
                    }
                  ]
                }
              }
            }
          }
        }
      }
    }
    
    debugLog(commit, 'ai', 'Component assembly complete', { finalData })
    
    return JSON.stringify(finalData)
  },

  // Load plan from history when user clicks on a prompt
  async loadPlanFromHistory({ commit, dispatch }, aiResponse) {
    debugLog(commit, 'history', 'Loading plan from history', { aiResponse })
    
    // Parse the AI response to extract the plan data
    let planData = null
    try {
      let jsonText = null
      if (aiResponse.plan) {
        jsonText = aiResponse.plan
      } else if (aiResponse.text) {
        jsonText = aiResponse.text
      }
      
      if (jsonText) {
        // Use XMBL util to validate JSON first
        const isValidJson = await dispatch('validjson', jsonText, { root: true })
        
        if (isValidJson) {
          planData = JSON.parse(jsonText)
        } else {
          // Try to extract JSON from markdown code blocks
          const jsonMatch = jsonText.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/)
          if (jsonMatch && jsonMatch[1]) {
            const extractedJson = jsonMatch[1]
            const isExtractedValid = await dispatch('validjson', extractedJson, { root: true })
            if (isExtractedValid) {
              planData = JSON.parse(extractedJson)
            }
          } else {
            // Try to find JSON object in the response
            const jsonObjectMatch = jsonText.match(/\{[\s\S]*\}/)
            if (jsonObjectMatch) {
              const objectJson = jsonObjectMatch[0]
              const isObjectValid = await dispatch('validjson', objectJson, { root: true })
              if (isObjectValid) {
                planData = JSON.parse(objectJson)
              }
            }
          }
        }
      }
      
      // Ensure phase is set for parsed data
      if (planData && !planData.phase) {
        planData.phase = 'intent'
      }
    } catch (e) {
      console.warn('Failed to parse AI response from history:', e)
      // If parsing fails, create a basic plan structure
      planData = {
        phase: 'intent',
        intent: aiResponse.text || 'Previous AI response',
        response: aiResponse.text || 'AI response loaded from history',
        capabilities: [],
        recommended_components: [],
        recommended_functions: [],
        recommended_actions: []
      }
    }
    
    // Store the loaded plan
    commit('set', {
      keys: ['terminal', 'lastPlan'],
      val: planData
    }, { root: true })
    
    debugLog(commit, 'history', 'Plan loaded from history', { planData })
  },

  // Validate AI recommendations and provide specific object keys for invalid choices
  async validateRecommendations({ commit }, { components, functions, actions, introspection }) {
    debugLog(commit, 'validation', 'Validating AI recommendations', { components, functions, actions })
    
    const validation = {
      components: { valid: [], invalid: [] },
      functions: { valid: [], invalid: [] },
      actions: { valid: [], invalid: [] },
      isValid: true,
      errors: [],
      availableKeys: {
        components: Object.keys(introspection.components || {}),
        functions: Object.keys(introspection.functions || {}),
        actions: introspection.dispatchActions?.map(a => a.name) || [],
        models: Object.keys(introspection.models || {}),
        pages: Object.keys(introspection.pages || {}),
        items: Object.keys(introspection.items || {})
      }
    }
    
    // Validate components
    components.forEach(comp => {
      if (validation.availableKeys.components.includes(comp)) {
        validation.components.valid.push({
          name: comp,
          props: introspection.components[comp]?.props || {},
          data: introspection.components[comp]
        })
      } else {
        validation.components.invalid.push(comp)
        validation.isValid = false
        validation.errors.push(`Invalid component: ${comp}. Available: ${validation.availableKeys.components.join(', ')}`)
      }
    })
    
    // Validate functions
    functions.forEach(func => {
      if (validation.availableKeys.functions.includes(func)) {
        validation.functions.valid.push({
          name: func,
          signature: introspection.functions[func]?.signature || 'Unknown signature',
          data: introspection.functions[func]
        })
      } else {
        validation.functions.invalid.push(func)
        validation.isValid = false
        validation.errors.push(`Invalid function: ${func}. Available: ${validation.availableKeys.functions.join(', ')}`)
      }
    })
    
    // Validate actions (both dispatch actions and functions that can be called with :is="action")
    actions.forEach(action => {
      const foundAction = introspection.dispatchActions?.find(a => a.name === action || a.action === action)
      const foundFunction = introspection.functions?.[action]
      
      if (foundAction) {
        validation.actions.valid.push({
          name: action,
          type: 'action',
          signature: foundAction.signature || 'Unknown signature',
          data: foundAction
        })
      } else if (foundFunction) {
        validation.actions.valid.push({
          name: action,
          type: 'function',
          signature: foundFunction.signature || 'Unknown signature',
          data: foundFunction
        })
      } else {
        validation.actions.invalid.push(action)
        validation.isValid = false
        validation.errors.push(`Invalid action: ${action}. Available actions: ${validation.availableKeys.actions.join(', ')}. Available functions: ${validation.availableKeys.functions.join(', ')}`)
      }
    })
    
    debugLog(commit, 'validation', 'Validation complete', { validation })
    
    return validation
  },

  // Compose page structure based on intent, introspection, and context
  async composePageStructure({ commit, dispatch, rootState }) {
    debugLog(commit, 'compose', 'Starting page composition', {})
    
    const lastPlan = rootState.terminal.lastPlan
    const intent = lastPlan?.intent || ''
    const introspection = rootState.agent.introspection
    
    // Load context.json and models.json
    const contextJson = await import('@/agent/context.json').then(m => m.default || m)
    const modelsJson = await import('@/agent/models.json').then(m => m.default || m)
    
    // Build composition prompt using introspection and context
    const composePrompt = `You are an AI assistant for XMBL. Compose a page structure based on the user's intent.

User Intent: "${intent}"

Available Components (from introspection):
${JSON.stringify(Object.keys(introspection.components || {}).slice(0, 50), null, 2)}

Component Descriptions (from context.json):
${JSON.stringify(contextJson.descriptions?.components || {}, null, 2)}

Page Model (from models.json):
${JSON.stringify(modelsJson.page || {}, null, 2)}

Recommended Components: ${JSON.stringify(lastPlan?.recommended_components || [])}

IMPORTANT RULES:
1. Components in a page's components array must be objects with: { name: "componentName", props: {}, styles: {} }
2. Use only components that exist in introspection
3. Infer props from component descriptions in context.json
4. For iform components, infer the 'title' prop from the intent (e.g., "contact form" -> title: "contact")
5. For ilist/browse components, infer the 'type' prop from app.itemTypes (must be exact match)
6. For ilink components, infer the 'to' prop (routes use history mode, no # sign)
7. Return ONLY valid JSON matching the page model structure

Return this JSON structure:
{
  "text": "Page title text",
  "styles": {
    "classes": "string of CSS classes",
    "style": {}
  },
  "components": [
    {
      "name": "componentName",
      "props": {},
      "styles": {}
    }
  ],
  "published": false,
  "menu": false,
  "automenu": false
}`

    const aiResponse = await dispatch('agent/callAI', { prompt: composePrompt })
    
    let pageData
    try {
      pageData = JSON.parse(aiResponse)
    } catch (e) {
      const jsonMatch = aiResponse.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/) || aiResponse.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        pageData = JSON.parse(jsonMatch[1] || jsonMatch[0])
      } else {
        throw new Error('Failed to parse composition response')
      }
    }
    
    // Ensure components are objects with name, props, styles
    if (pageData.components && Array.isArray(pageData.components)) {
      pageData.components = pageData.components.map(comp => {
        if (typeof comp === 'string') {
          return { name: comp, props: {}, styles: {} }
        }
        if (!comp.name) {
          return { name: comp.component || comp, props: comp.props || {}, styles: comp.styles || {} }
        }
        return {
          name: comp.name,
          props: comp.props || {},
          styles: comp.styles || {}
        }
      })
    }
    
    // Infer props for components using constructComponentProps
    for (let i = 0; i < pageData.components.length; i++) {
      const comp = pageData.components[i]
      const inferredProps = await dispatch('agent/constructComponentProps', {
        componentName: comp.name,
        props: comp.props,
        userIntent: intent,
        introspection,
        rootState
      }, { root: true })
      pageData.components[i].props = { ...comp.props, ...inferredProps }
    }
    
    // Generate page name from intent
    const pageName = intent.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50) || 'new-page'
    
    // Store composed structure
    commit('set', {
      keys: ['terminal', 'lastPlan', 'composedStructure'],
      val: { type: 'page', name: pageName, data: pageData }
    }, { root: true })
    
    // Store preview page data
    commit('set', {
      keys: ['terminal', 'previewPage'],
      val: { name: pageName, data: pageData }
    }, { root: true })
    
    // Move to complete phase
    commit('set', {
      keys: ['terminal', 'lastPlan', 'phase'],
      val: 'complete'
    }, { root: true })
    
    debugLog(commit, 'compose', 'Page composition complete', { pageName, componentCount: pageData.components?.length || 0 })
  },

  // Construct component props based on introspection and context
  async constructComponentProps({ commit }, { componentName, props, userIntent, rootState }) {
    const inferredProps = { ...props }
    
    // Load context.json for component descriptions
    await import('@/agent/context.json').then(m => m.default || m)
    
    if (componentName === 'iform') {
      // iform needs 'title' prop - infer from user intent
      if (!inferredProps.title) {
        const formMatch = userIntent.match(/(?:form|create|add)\s+(?:a\s+)?(?:new\s+)?(\w+)/i)
        if (formMatch) {
          inferredProps.title = formMatch[1].toLowerCase()
        } else if (userIntent.toLowerCase().includes('contact')) {
          inferredProps.title = 'contact'
        }
      }
    } else if (componentName === 'ilist' || componentName === 'browse') {
      // ilist and browse need type prop - must be exact item type from app.itemTypes
      if (!inferredProps.type) {
        const stateItemTypes = rootState.app?.itemTypes || []
        const itemTypeNames = stateItemTypes.map(type => type.name.toLowerCase())
        const typeMatch = userIntent.match(/(?:list|show|display)\s+(?:of\s+)?(\w+)/i)
        const inferredType = typeMatch ? typeMatch[1].toLowerCase() : null
        
        if (inferredType && itemTypeNames.includes(inferredType)) {
          inferredProps.type = inferredType
        } else {
          debugLog(commit, 'compose', `Could not infer valid item type for ${componentName}`, { userIntent, availableItemTypes: itemTypeNames })
        }
      }
    } else if (componentName === 'ilink') {
      // ilink needs 'to' prop - routes don't use #, just path
      if (!inferredProps.to) {
        const routeMatch = userIntent.match(/(?:go to|navigate to|link to|page)\s+([\w-]+)/i)
        inferredProps.to = routeMatch ? `/${routeMatch[1]}` : '/'
      }
    }
    
    return inferredProps
  },

  async createFunctionFromAI({ commit, rootState, dispatch }, { functionDef }) {
    if (!functionDef || !functionDef.name) {
      console.warn('[agent] createFunctionFromAI: Invalid function definition')
      return
    }

    // Check if function already exists
    const existingFuncId = rootState.ref?.functions && rootState.ref.functions[functionDef.name]
    if (existingFuncId) {
      console.log('[agent] Function already exists:', functionDef.name)
      return existingFuncId
    }

    // Ensure functions object exists
    if (!rootState.app.advanced) {
      commit('set', { keys: ['app', 'advanced'], val: {} }, { root: true })
    }
    if (!rootState.app.advanced.functions) {
      commit('set', { keys: ['app', 'advanced', 'functions'], val: {} }, { root: true })
    }
    if (!rootState.ref.functions) {
      commit('set', { keys: ['ref', 'functions'], val: {} }, { root: true })
    }

    // Normalize commands to ensure they have required fields
    const normalizedCommands = (functionDef.commands || []).map((cmd) => {
      const normalized = { ...cmd }
      
      // Ensure method field exists - construct from action/type if needed
      if (!normalized.method) {
        if (normalized.action && normalized.type) {
          // For app-type commands, method should be dispatch.action or commit.action
          // commit.set is used for Vuex mutations, dispatch.* for actions
          const storeMethod = normalized.action === 'set' ? 'commit' : (normalized.type === 'app' ? 'dispatch' : 'commit')
          normalized.method = `${storeMethod}.${normalized.action}`
        } else if (normalized.action) {
          // Fallback: use action as method if type is missing
          normalized.method = normalized.action
        } else {
          // Default empty method - will show warning in UI
          normalized.method = ''
        }
      }
      
      // Normalize commit.set commands for function updates
      if (normalized.method === 'commit.set' && normalized.payload && normalized.payload.keys) {
        // Ensure payload has proper structure for commit.set
        if (!normalized.payload.val && normalized.payload.value) {
          normalized.payload.val = normalized.payload.value
          delete normalized.payload.value
        }
      }
      
      // Ensure type field exists
      if (!normalized.type) {
        normalized.type = 'app'
      }
      
      // Ensure payload exists
      if (!normalized.payload) {
        normalized.payload = {}
      }
      
      // Ensure inputs exists
      if (!normalized.inputs) {
        normalized.inputs = {}
      }
      
      return normalized
    })

    // Create function structure matching XMBL function schema
    const functionData = {
      name: functionDef.name,
      description: functionDef.description || `AI-generated function: ${functionDef.name}`,
      inputs: functionDef.inputs || {},
      commands: normalizedCommands,
      output: functionDef.output || {},
      test: functionDef.test || [],
      show: functionDef.show || {},
      state: functionDef.state || {}
    }

    // Generate hash for function ID
    const hash = await dispatch('util', { type: 'hash', body: functionData }, { root: true })

    // Store function in state
    commit('set', { keys: ['app', 'advanced', 'functions', hash], val: functionData }, { root: true })
    commit('set', { keys: ['ref', 'functions', functionDef.name], val: hash }, { root: true })

    // Save to database
    await dispatch('update', { type: 'app', data: rootState.app }, { root: true })

    console.log('[agent] Created function from AI:', functionDef.name, hash)
    debugLog(commit, 'function', 'Function created from AI response', { functionName: functionDef.name, hash })
    return hash
  }
}



export const agent = {
  namespaced: true,
  state,
  mutations,
  actions
}

